import React, { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const amount = Number(payload[0].value) || 0;
  return (
    <div className="mvp-cd-tip">
      <strong>{label}</strong>
      <span>Revenue : {amount.toFixed(2)}</span>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function monthKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

export default function MvpClientRevenueChart({ invoices = [] }) {
  const data = useMemo(() => {
    const now = new Date();
    const points = [];
    for (let offset = 5; offset >= 0; offset -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      points.push({
        key: monthKey(date),
        month: MONTHS[date.getMonth()],
        amount: 0,
      });
    }
    const byKey = new Map(points.map((point) => [point.key, point]));
    invoices.forEach((invoice) => {
      const raw = invoice.cli_inv_issue_date || invoice.cli_inv_created_at || invoice.created_at;
      const date = raw ? new Date(raw) : null;
      if (!date || Number.isNaN(date.getTime())) return;
      const point = byKey.get(monthKey(date));
      if (!point) return;
      point.amount += Number(invoice.cli_inv_total_amount) || 0;
    });
    return points;
  }, [invoices]);

  return (
    <div className="mvp-cd-chart-plot">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 18, right: 8, left: -12, bottom: 0 }}>
          <defs>
            <linearGradient id="mvpClientRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9AABFF" stopOpacity={0.42} />
              <stop offset="100%" stopColor="#D5DCFF" stopOpacity={0.28} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke="#E4E8EE" vertical />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#9AA3AE", fontSize: 13, fontFamily: "halyard-display, sans-serif" }}
            dy={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#9AA3AE", fontSize: 12, fontFamily: "halyard-display, sans-serif" }}
            width={40}
            tickFormatter={(value) => {
              if (value >= 1000000) return `${Math.round(value / 1000000)}M`;
              if (value >= 1000) return `${Math.round(value / 1000)}k`;
              return value;
            }}
          />
          <Tooltip
            content={<ChartTooltip />}
            cursor={{ stroke: "#CBD5E1", strokeWidth: 1, strokeDasharray: "3 3" }}
          />
          <Area
            type="linear"
            dataKey="amount"
            stroke="rgba(100, 121, 248, 0.60)"
            strokeWidth={0.5}
            fill="url(#mvpClientRevenue)"
            fillOpacity={1}
            dot={{ r: 3, fill: "#6479F8", stroke: "#6479F8", strokeWidth: 0 }}
            activeDot={{ r: 3, fill: "#6479F8", stroke: "#6479F8", strokeWidth: 0 }}
            isAnimationActive
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
